// apps/web/src/api/russia-requests/files.ts
//
// Typed wrappers for the Russia COMPLEX request file-attachment
// endpoints (#1546). Mirrors the China `api/requests/files.ts`
// layout — the wire shapes come straight from the shared
// `RussiaRequestFile*` schemas.

import { z } from 'zod';
import {
  RussiaRequestFileSchema,
  StorageDownloadUrlResponseSchema,
  type RussiaRequestFile,
  type RussiaRequestFileCreateInput,
  type StorageDownloadUrlResponse,
} from '@magimp/shared';
import { request } from '../client.js';

// WHY: the list endpoint returns a bare array of the read wire shape
//   (`objectKey` omitted). Build the array parser from the shared row
//   schema rather than re-declaring the row fields.
const RussiaRequestFileListSchema = z.array(RussiaRequestFileSchema);

/**
 * @aiPurpose `GET /russia-requests/:id/files` — live (non-soft-deleted)
 *   file rows for the request, ordered by createdAt ascending. Backend
 *   perm `russiaRequests.read`.
 */
export function listRussiaRequestFiles(requestId: string): Promise<RussiaRequestFile[]> {
  return request<RussiaRequestFile[]>(
    `/russia-requests/${encodeURIComponent(requestId)}/files`,
    { method: 'GET' },
    {},
    RussiaRequestFileListSchema,
  );
}

/**
 * @aiPurpose `POST /russia-requests/:id/files` — record the metadata row
 *   that binds an already-PUT `request-attachment/...` object key to the
 *   request. The bytes were uploaded via the presigned URL beforehand.
 *   Backend perm `russiaRequests.manage`.
 */
export function bindRussiaRequestFile(
  requestId: string,
  input: RussiaRequestFileCreateInput,
): Promise<RussiaRequestFile> {
  return request<RussiaRequestFile>(
    `/russia-requests/${encodeURIComponent(requestId)}/files`,
    {
      method: 'POST',
      body: JSON.stringify(input),
      headers: { 'content-type': 'application/json' },
    },
    {},
    RussiaRequestFileSchema,
  );
}

/**
 * @aiPurpose `POST /russia-requests/:id/files/:fileId/download-url` — mint
 *   a short-TTL presigned download URL for a live file. The raw storage
 *   key never leaves the backend; the server resolves it by file id.
 *   Backend perm `russiaRequests.read`.
 * @aiSecurity the returned `downloadUrl` is bearer-like — keep it in
 *   in-memory refs only; never log/persist it.
 */
export function russiaRequestFileDownloadUrl(
  requestId: string,
  fileId: string,
): Promise<StorageDownloadUrlResponse> {
  // WHY: parameterless POST — the backend reads only the route params, so
  //   the request carries no body. Sending `content-type: application/json`
  //   with an empty body makes Fastify reject it
  //   (FST_ERR_CTP_EMPTY_JSON_BODY → 500). Omit the header, mirroring the
  //   China-side `requestFileDownloadUrl`.
  return request<StorageDownloadUrlResponse>(
    `/russia-requests/${encodeURIComponent(requestId)}/files/${encodeURIComponent(
      fileId,
    )}/download-url`,
    { method: 'POST' },
    {},
    StorageDownloadUrlResponseSchema,
  );
}

/**
 * @aiPurpose `DELETE /russia-requests/:id/files/:fileId` — soft-delete a
 *   file; resolves on 204. Idempotent: a second delete still resolves.
 *   Backend perm `russiaRequests.manage`.
 */
export function deleteRussiaRequestFile(requestId: string, fileId: string): Promise<void> {
  return request<void>(
    `/russia-requests/${encodeURIComponent(requestId)}/files/${encodeURIComponent(fileId)}`,
    { method: 'DELETE' },
    {},
    z.void(),
  );
}
